import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { getRiskConfig, updateRiskConfig, getReminderSettings, updateReminderSettings, getRiskQuestions, updateRiskQuestions } from "../../lib/api";
import { setRiskBands, RISK_LEVELS } from "../risk/riskConstants";

const PALETTE = [
  "bg-green-100 text-green-800",
  "bg-amber-100 text-amber-800",
  "bg-orange-100 text-orange-800",
  "bg-red-100 text-red-800",
];

function WeeklyPromptCard() {
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getReminderSettings()
      .then((r) => setEnabled(r.data.risk_prompt_enabled))
      .catch(() => toast.error("Failed to load prompt setting"))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (next) => {
    setEnabled(next);
    setSaving(true);
    try {
      const res = await updateReminderSettings({ risk_prompt_enabled: next });
      setEnabled(res.data.risk_prompt_enabled);
      toast.success(next ? "Weekly prompt enabled" : "Weekly prompt disabled");
    } catch (e) {
      setEnabled(!next);
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="risk-prompt-card">
      <CardHeader>
        <CardTitle>Weekly Risk Assessment Prompt</CardTitle>
        <CardDescription>
          When on, every active staff member gets a once-per-week in-app reminder to submit their risk assessment on the last school day of the week (usually Friday).
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between p-4 border rounded-lg">
          <div className="space-y-1">
            <Label className="font-medium">Send weekly prompt to staff</Label>
            <p className="text-sm text-muted-foreground">Turn off to stop the automatic weekly reminder entirely.</p>
          </div>
          <Switch checked={enabled} onCheckedChange={toggle} disabled={loading || saving} data-testid="risk-prompt-toggle" />
        </div>
      </CardContent>
    </Card>
  );
}

const emptyDescriptions = () => ({ 1: "", 2: "", 3: "", 4: "", 5: "" });

function QuestionsEditorCard() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getRiskQuestions();
      setQuestions((res.data.categories || []).map((c) => ({
        key: c.key,
        label: c.label,
        descriptions: { 1: "", 2: "", 3: "", 4: "", 5: "", ...(c.descriptions || {}) },
      })));
    } catch {
      toast.error("Failed to load risk questions");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const editLabel = (idx, val) =>
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, label: val } : q)));
  const editDesc = (idx, lvl, val) =>
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, descriptions: { ...q.descriptions, [lvl]: val } } : q)));
  const addQuestion = () =>
    setQuestions((prev) => [...prev, { label: "", descriptions: emptyDescriptions() }]);
  const removeQuestion = (idx) =>
    setQuestions((prev) => prev.filter((_, i) => i !== idx));

  const save = async () => {
    const cleaned = questions
      .map((q) => ({ key: q.key, label: (q.label || "").trim(), descriptions: q.descriptions }))
      .filter((q) => q.label);
    if (cleaned.length === 0) {
      toast.error("Add at least one question with a label");
      return;
    }
    setSaving(true);
    try {
      const res = await updateRiskQuestions(cleaned);
      setQuestions((res.data.categories || []).map((c) => ({
        key: c.key, label: c.label,
        descriptions: { 1: "", 2: "", 3: "", 4: "", 5: "", ...(c.descriptions || {}) },
      })));
      toast.success("Risk assessment questions saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save questions");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card><CardContent className="py-8 text-center text-muted-foreground">Loading questions…</CardContent></Card>;

  return (
    <Card data-testid="risk-questions-card">
      <CardHeader>
        <CardTitle>Risk Assessment Questions</CardTitle>
        <CardDescription>
          Add, remove, or edit the questions (categories) staff score each week. Each question is rated 1–5; a staff member&rsquo;s total is the sum of all questions (max = 5 × number of questions). Editing wording keeps past submissions intact.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {questions.map((q, idx) => (
          <div key={idx} className="border rounded-lg p-4 space-y-3" data-testid={`risk-question-${idx}`}>
            <div className="flex items-end gap-3">
              <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-[#00305E]/10 text-[#00305E] whitespace-nowrap">Question {idx + 1}</span>
              <div className="flex-1">
                <label className="text-xs text-muted-foreground">Question / Category name</label>
                <Input value={q.label} onChange={(e) => editLabel(idx, e.target.value)} placeholder="e.g. Injury / Illness Issues" data-testid={`risk-question-label-${idx}`} />
              </div>
              <Button variant="ghost" size="sm" onClick={() => removeQuestion(idx)} disabled={questions.length <= 1} className="text-red-600 hover:text-red-700" data-testid={`risk-question-remove-${idx}`}>
                Remove
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {RISK_LEVELS.map((lvl) => (
                <div key={lvl.value} className="flex gap-2 items-start">
                  <span className="text-xs font-medium w-24 pt-2 text-muted-foreground shrink-0">{lvl.value} — {lvl.label}</span>
                  <Textarea
                    value={q.descriptions[lvl.value] || ""}
                    onChange={(e) => editDesc(idx, lvl.value, e.target.value)}
                    placeholder={`What a ${lvl.label.toLowerCase()} (${lvl.value}) week looks like…`}
                    className="min-h-[52px] text-sm"
                    data-testid={`risk-question-${idx}-desc-${lvl.value}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}
        <Button variant="outline" onClick={addQuestion} data-testid="risk-question-add-btn">+ Add Question</Button>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={load} disabled={saving}>Reset</Button>
          <Button onClick={save} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="risk-questions-save-btn">
            {saving ? "Saving…" : "Save Questions"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function RiskConfigTab() {
  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await getRiskConfig();
      setBands(res.data.bands || []);
    } catch {
      toast.error("Failed to load risk bands");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateBand = (idx, field, value) => {
    setBands((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)));
  };

  const save = async () => {
    const cleaned = bands
      .map((b) => ({ max: parseInt(b.max, 10), label: (b.label || "").trim() }))
      .filter((b) => !Number.isNaN(b.max) && b.label);
    if (cleaned.length === 0) {
      toast.error("Add at least one valid band");
      return;
    }
    setSaving(true);
    try {
      const res = await updateRiskConfig(cleaned);
      setRiskBands(res.data.bands);
      setBands(res.data.bands);
      toast.success("Risk bands updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Card><CardContent className="py-8 text-center text-muted-foreground">Loading…</CardContent></Card>;

  const sorted = [...bands].sort((a, b) => (a.max || 0) - (b.max || 0));

  return (
    <div className="space-y-6" data-testid="risk-config-tab">
      <WeeklyPromptCard />
      <QuestionsEditorCard />
      <Card>
        <CardHeader>
          <CardTitle>Risk Assessment Score Bands</CardTitle>
          <CardDescription>
            Set the upper cutoff for each severity band — a total at or below a band&rsquo;s cutoff falls into that band. The maximum total depends on how many questions you have (5 × number of questions).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            {bands.map((b, idx) => (
              <div key={idx} className="flex items-end gap-3 border rounded-lg p-3" data-testid={`risk-band-row-${idx}`}>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${PALETTE[Math.min(idx, PALETTE.length - 1)]}`}>
                  Band {idx + 1}
                </span>
                <div className="flex-1">
                  <label className="text-xs text-muted-foreground">Label</label>
                  <Input value={b.label} onChange={(e) => updateBand(idx, "label", e.target.value)} data-testid={`risk-band-label-${idx}`} />
                </div>
                <div className="w-28">
                  <label className="text-xs text-muted-foreground">Up to (≤)</label>
                  <Input type="number" min="0" max="50" value={b.max} onChange={(e) => updateBand(idx, "max", e.target.value)} data-testid={`risk-band-max-${idx}`} />
                </div>
              </div>
            ))}
          </div>

          <div className="bg-muted/50 rounded-lg p-3 text-sm text-muted-foreground">
            Preview:{" "}
            {sorted.map((b, i) => {
              const lo = i === 0 ? 0 : (sorted[i - 1].max || 0) + 1;
              return (
                <span key={i} className="mr-3">
                  <strong>{b.label}</strong> {lo}–{b.max}
                </span>
              );
            })}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={load} disabled={saving}>Reset</Button>
            <Button onClick={save} disabled={saving} className="bg-[#00305E] hover:bg-[#00305E]/90" data-testid="risk-config-save-btn">
              {saving ? "Saving…" : "Save Bands"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
